import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { apiGet } from "@/lib/api";
import {
  LayoutDashboard, Users, Building2, CreditCard, BarChart2,
  Bell, User, Upload, Receipt, LogOut, ChevronRight, Menu, X,
  ShieldCheck, UserCheck, ClipboardList, Car
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const PAGE_INFO = {
  "/":                { title: "Dashboard",           sub: "Your residence overview" },
  "/residents":       { title: "Residents",           sub: "Manage resident accounts" },
  "/units":           { title: "Houses",              sub: "House occupancy overview" },
  "/payments":        { title: "Payment Verification",sub: "Review and approve payments" },
  "/vehicles":        { title: "Registered Vehicles", sub: "Search sticker-registered vehicles" },
  "/reports":         { title: "Reports",             sub: "Analytics & exports" },
  "/notifications":   { title: "Notifications",       sub: "Your notifications" },
  "/submit-payment":  { title: "Submit Payment",      sub: "Upload your payment receipt" },
  "/my-payments":     { title: "My Payments",         sub: "Your payment history" },
  "/profile":         { title: "My Profile",          sub: "Your account details" },
  "/security":        { title: "Security Dashboard",  sub: "Visitor check-in & check-out" },
  "/my-visitors":     { title: "My Visitors",         sub: "Manage visitor invitations" },
  "/visitor-log":     { title: "Visitor Log",         sub: "Full visitor history & blacklist" },
};

function getInitials(name = "") {
  return name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [counts, setCounts] = useState({ pendingResidents: 0, pendingPayments: 0, unreadNotifs: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const pageInfo = PAGE_INFO[location.pathname] || { title: "PERCASA", sub: "" };
  const today = new Date().toLocaleDateString("en-MY", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  useEffect(() => {
    fetchCounts();
  }, [location.pathname]);

  async function fetchCounts() {
    try {
      const [statsRes, notifsRes] = await Promise.all([
        apiGet("/stats"),
        apiGet("/notifications"),
      ]);
      const s = statsRes.data;
      const unread = notifsRes.data.filter(n => !n.is_read).length;
      setCounts({
        pendingResidents: s.pending_residents || 0,
        pendingPayments: s.pending_payments || 0,
        unreadNotifs: unread,
      });
    } catch (e) {}
  }

  const isAdmin    = user?.role === "admin";
  const isSecurity = user?.role === "security";
  const isResident = user?.role === "resident";

  // Pending approval page
  if (user?.status === "pending") {
    return (
      <div className="pending-page">
        <div className="pending-box">
          <div className="pending-icon">
            <Bell size={40} color="var(--pending)" />
          </div>
          <div className="pending-title">Registration Pending</div>
          <div className="pending-desc">
            Your account is awaiting admin approval. You'll receive a notification once approved.<br /><br />
            <strong>Email:</strong> {user.email}
          </div>
          <button className="btn btn-primary" style={{ marginTop: 20, width: "100%" }} onClick={logout} data-testid="pending-logout-btn">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="percasa-shell">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-logo">
          <BrandLogo variant="sidebar" />
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-section-label">Overview</div>

          <NavLink to="/" end className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-dashboard">
            <LayoutDashboard className="nav-icon" size={16} />
            Dashboard
          </NavLink>

          {isAdmin && (
            <>
              <div className="sidebar-section-label">Admin</div>
              <NavLink to="/residents" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-residents" onClick={() => setSidebarOpen(false)}>
                <Users className="nav-icon" size={16} />
                Residents
                {counts.pendingResidents > 0 && <span className="nav-badge warn">{counts.pendingResidents}</span>}
              </NavLink>
              <NavLink to="/units" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-units" onClick={() => setSidebarOpen(false)}>
                <Building2 className="nav-icon" size={16} />
                Houses
              </NavLink>
              <NavLink to="/payments" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-payments" onClick={() => setSidebarOpen(false)}>
                <CreditCard className="nav-icon" size={16} />
                Payments
                {counts.pendingPayments > 0 && <span className="nav-badge">{counts.pendingPayments}</span>}
              </NavLink>
              <NavLink to="/vehicles" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-vehicles" onClick={() => setSidebarOpen(false)}>
                <Car className="nav-icon" size={16} />
                Vehicles
              </NavLink>
              <NavLink to="/reports" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-reports" onClick={() => setSidebarOpen(false)}>
                <BarChart2 className="nav-icon" size={16} />
                Reports
              </NavLink>
              <div className="sidebar-section-label">Visitors</div>
              <NavLink to="/visitor-log" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-visitor-log" onClick={() => setSidebarOpen(false)}>
                <ClipboardList className="nav-icon" size={16} />
                Visitor Log
              </NavLink>
            </>
          )}

          {isSecurity && (
            <>
              <div className="sidebar-section-label">Security</div>
              <NavLink to="/security" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-security" onClick={() => setSidebarOpen(false)}>
                <ShieldCheck className="nav-icon" size={16} />
                Visitor Check-In
              </NavLink>
              <NavLink to="/visitor-log" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-visitor-log-sec" onClick={() => setSidebarOpen(false)}>
                <ClipboardList className="nav-icon" size={16} />
                Visitor Log
              </NavLink>
              <NavLink to="/vehicles" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-vehicles-sec" onClick={() => setSidebarOpen(false)}>
                <Car className="nav-icon" size={16} />
                Vehicles
              </NavLink>
            </>
          )}

          {isResident && (
            <>
              <div className="sidebar-section-label">Resident</div>
              <NavLink to="/submit-payment" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-submit-payment" onClick={() => setSidebarOpen(false)}>
                <Upload className="nav-icon" size={16} />
                Submit Payment
              </NavLink>
              <NavLink to="/my-payments" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-my-payments" onClick={() => setSidebarOpen(false)}>
                <Receipt className="nav-icon" size={16} />
                My Payments
              </NavLink>
              <NavLink to="/my-visitors" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-my-visitors" onClick={() => setSidebarOpen(false)}>
                <UserCheck className="nav-icon" size={16} />
                My Visitors
                <span className="nav-badge coming-soon">Soon</span>
              </NavLink>
            </>
          )}

          <div className="sidebar-divider" />

          <NavLink to="/notifications" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-notifications" onClick={() => setSidebarOpen(false)}>
            <Bell className="nav-icon" size={16} />
            Notifications
            {counts.unreadNotifs > 0 && <span className="nav-badge unread">{counts.unreadNotifs}</span>}
          </NavLink>
          <NavLink to="/profile" className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`} data-testid="nav-profile" onClick={() => setSidebarOpen(false)}>
            <User className="nav-icon" size={16} />
            Profile
          </NavLink>
        </nav>

        <div className="sidebar-user">
          <div className="sidebar-avatar">{getInitials(user?.name)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sidebar-user-name">{user?.name}</div>
            <div className="sidebar-user-role">{user?.role}</div>
          </div>
          <button className="sidebar-logout-btn" onClick={logout} title="Sign out" data-testid="logout-btn">
            <LogOut size={14} />
          </button>
        </div>
      </div>

      {/* Main */}
      <div className="percasa-main">
        <div className="topbar">
          <div className="topbar-left">
            <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)} data-testid="hamburger-btn">
              {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div className="topbar-title">{pageInfo.title}</div>
            <div className="topbar-sub">{today} · PERCASA Residence</div>
          </div>
          <div className="topbar-right">
            <div className="notif-btn" onClick={() => navigate("/notifications")} data-testid="topbar-notif-btn">
              <Bell size={16} />
              {counts.unreadNotifs > 0 && <div className="notif-dot" />}
            </div>
            <div className="topbar-avatar" onClick={() => navigate("/profile")} title={user?.name} data-testid="topbar-avatar">
              {getInitials(user?.name)}
            </div>
          </div>
        </div>

        <div className="page-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
